import { ChevronRight } from "lucide-react";
import { useMemo, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  type PaymentRead,
  type PaymentStatus,
  type PaymentType,
} from "@/api-v3";
import { Badge } from "@/components/ui/badge";
import { PaymentTypeIcon } from "@/features/payments/components/payment-type-icon";
import { getStatusColor } from "@/features/payments/helpers/get-status-color";
import { paymentTypeToRoute } from "@/features/payments/helpers/payment-type-to-route";
import { isFailedPayoutStatus } from "@/features/payments/helpers/payout-status";
import { formatCurrency } from "@/lib/currency-formatter";
import { cn, formatKeyText } from "@/lib/utils";

import { isPaymentTypeWithAction } from "../types/payment-types";

interface PaymentProps {
  id: PaymentRead["id"];
  title: string;
  subtitle: string;
  amount: PaymentRead["amount"];
  currency: PaymentRead["currency"];
  status: PaymentStatus;
  type: PaymentType;
  payoutStatus?: PaymentRead["payout_status"];
  raisedByStudyTeam?: boolean;
}

export function PaymentListItemComponent({
  id,
  title,
  subtitle,
  amount,
  currency,
  status,
  type,
  payoutStatus,
  raisedByStudyTeam = false,
}: PaymentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const currencyDisplay = useMemo(
    () => formatCurrency(amount, currency),
    [amount, currency]
  );

  const needsAction = isPaymentTypeWithAction(type) && status === "pending";
  const payoutFailed = isFailedPayoutStatus(payoutStatus);

  const navigateToDetails = () => navigate(paymentTypeToRoute(type, id));

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      navigateToDetails();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${t("payment_title")} ${title}`}
      onClick={navigateToDetails}
      onKeyDown={handleKeyDown}
      className={cn(
        "relative w-full text-left bg-white rounded-2xl border border-border p-5 shadow-xs",
        "hover:shadow-md hover:border-primary/40 transition-all cursor-pointer",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <PaymentTypeIcon type={type} />
          <div className="min-w-0 pt-1">
            <h3 className="text-lg font-semibold leading-tight truncate">
              {title}
            </h3>
            {(needsAction || payoutFailed || raisedByStudyTeam) && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {needsAction && (
                  <Badge variant="attention">
                    {t("payments_actionRequired")}
                  </Badge>
                )}
                {payoutFailed && (
                  <Badge variant="destructive">
                    {t("payments_paymentFailed")}
                  </Badge>
                )}
                {raisedByStudyTeam && (
                  <Badge variant="secondary">
                    {t("payments_from_study_team")}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
        <Badge className={cn("shrink-0", getStatusColor(status))}>
          {formatKeyText(t(status))}
        </Badge>
      </div>
      <div className="flex justify-between items-end gap-3">
        <p className="text-sm text-muted-foreground">{subtitle}</p>
        <div className="flex items-center gap-2">
          <p className="text-2xl font-semibold font-mono">{currencyDisplay}</p>
          <ChevronRight
            className="h-5 w-5 text-muted-foreground shrink-0"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}
